/**
 * Component: Redirect modal
 * ------------------------------------------------------------------------------
 * Redirect modal configuration for complete theme support.
 * - https://github.com/Shopify/theme-scripts/tree/master/packages/theme-sections
 *
 * @namespace redirectModal
 */
import Cookies from 'js-cookie';
import getUserLocale from 'get-user-locale';

import cssClasses from '~/helpers/cssClasses';
import Redirects from '~/helpers/redirects';
import { formToJSON, getQueryParamsObject, on, serialize } from '~/helpers/utils';

import Modal from '~/components/modal';

/**
 * DOM selectors.
 */
const selectors = {
  choiceContainer: '[js-redirect-modal="choiceContainer"]',
  choiceContinue: '[js-redirect-modal="choiceContinue"]',
  close: '[js-redirect-modal="close"]',
  container: '[js-redirect-modal="container"]',
  countrySelector: '[js-redirect-modal="countrySelector"]',
  footer: '[js-redirect-modal="footer"]',
  form: '[js-redirect-modal="form"]',
  languageSelectorContainer: '[js-redirect-modal="languageSelectorContainer"]',
  languageSelector: '[js-redirect-modal="languageSelector"]',
  icon: '[js-redirect-modal="icon"]',
  redirectLink: '[js-redirect-modal="redirectLink"]',
  settingsButton: '[js-redirect-modal="settingsButton"]',
  settingsContainer: '[js-redirect-modal="settingsContainer"]',
  updateButton: '[js-redirect-modal="updateButton"]',
};

/**
 * Attribute and data selectors.
 */
const dataSelectors = {
  redirectModal: 'js-redirect-modal',
};

/**
 * Instance cookies.
 */
const cookieSelector = {
  geolocation: 'redirect_geolocation_v3',
  modal: 'redirect_modal_v3',
  settings: 'redirect_store_settings_v3',
};

/**
 * Instance events.
 */
const eventSelector = {
  geoIPErrorHandler: 'Redirect:geoIPErrorHandler',
  locationDetected: 'Redirect:locationDetected',
  matched: 'Redirect:isMatchedLocation',
  modalClose: 'Modal:Close:redirect-modal',
  modalShow: 'Modal:Show:redirect-modal',
  redirectIcon: 'Redirect:updateIcon',
  unmatched: 'Redirect:isUnmatchedLocation',
};

/**
 * Instance settings.
 */
const settings = {
  id: 'redirect-modal',
};

/**
 * HTML placeholder strings used in language settings.
 */
const placeholder = {
  currentStoreName: '%current_store_name%',
  redirectStoreCode: '%redirect_store_code%',
  redirectStoreName: '%redirect_store_name%',
  redirectStoreUrl: '%redirect_store_url%',
};

/**
 * Create a new redirect modal.
 */
export default () => {

  /**
   * Instance globals.
   */
  const container = document.querySelector(selectors.container);
  const modal = Modal({
    id: settings.id,
  });

  /**
   * Initialise section objects.
   */
  function init() {
    modal.init();
    translateModal();
    renderCountries();
    renderTemplates();
    setEventListeners();
    setEventBusListeners();
    setDefaultState();
  }

  /**
   * Hide choice panel if user has already dismissed the modal or made their selection.
   */
  function setDefaultState() {
    const queryParameters = getQueryParamsObject();

    if (queryParameters.loc && queryParameters.lang) {
      handleParameterEvents(queryParameters);
    }
  }

  /**
   * Set event listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen(eventSelector.modalClose, () => handleCloseEvents());

    Frame.EventBus.listen(eventSelector.unmatched, () => {
      handleLocationParameterEvents();

      if (typeof Cookies.get(cookieSelector.modal) === 'undefined') {
        modal.show(settings.id);
      }
    });

    Frame.EventBus.listen(eventSelector.locationDetected, (response) => {
      if (typeof Cookies.getJSON(cookieSelector.settings) !== 'undefined') {
        return;
      }

      render(response);
      renderTemplates();
      var country = response.matchingStore.storeCode === 'EU' ? response.location.countryCode : response.matchingStore.storeCode;
      setFlag(country);
      setCountry(country);
      updateOptions(response.matchingStore);
      setEventListeners();
    });

    Frame.EventBus.listen(eventSelector.geoIPErrorHandler, () => {
      handleCloseEvents();
    });
  }

  /**
   * Set click events that switch between the two redirect views.
   */
  function setEventListeners() {
    on('click', container, (event) => handleClickEvents(event));
    on('submit', document.querySelector(selectors.form), (event) => handleFormSubmit(event));
    on('change', document.querySelector(selectors.countrySelector), (event) => handleCountryChange(event));
  }

  /**
   * Use event delegation on user click events on modal container.
   * @param {Object} event - Click event target on container.
   */
  function handleClickEvents(event) {
    if (isContinueBrowsingButton(event.target.parentNode)) {
      handleContinueClick(event);

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '06',
        eventCategory: 'change_site_popup',
        eventAction: 'stay_on_current_site',
        eventLabel: 'click',
      });
      return;
    }

    if (isRedirectChoice(event.target.parentNode)) {
      event.preventDefault();
      handleURLRedirection(document.querySelector(selectors.form));
    }

    if (isCloseButton(event.target)) {
      event.preventDefault();
      handleCloseButtonEvent(event);
    }
  }

  /**
   * Render the list of countries in the dropdown.
   */
  function renderCountries() {
    document.querySelector(selectors.countrySelector).innerHTML = getCountriesTemplate(supportedCountriesList());
  }

  /**
   * Return the html for the dropdown.
   * @param {Object} options - list of supported countries
   */
  function getCountriesTemplate(options) {
    return options.map((option) => {
      if (option.currency) {
        return `
        <option value="${option.countryCode}">
          ${option.countryName} (${option.currency})
        </option>`;
      } else {
        return `
        <option value="${option.countryCode}">
          ${option.countryName}
        </option>`;
      }
    }).join('');
  }

  /**
   * Filter the stores list and match the country codes with the country names.
   */
  function supportedCountriesList() {
    const countriesList = [];

    Frame.MultiStore.forEach((element) => {
      const supportedCountries = element.supportedStores.split(', ');

      supportedCountries.forEach((supportedCountry) => {
        const countryObject = Frame.CountriesList.filter((country) => {
          return country.countryCode === supportedCountry.toString();
        })[0];

        countryObject.currency = element.supportedCurrencies;

        countriesList.push(countryObject);
      });
    });

    return reorderCountries(countriesList);
  }

  /**
   * Alphabetically reorder the countries list.
   * @param {Object} countriesList
   */
  function reorderCountries(countriesList) {
    countriesList.sort((countryOne, countryTwo) => {
      if (countryOne.countryName > countryTwo.countryName) {
        return 1;
      } if (countryTwo.countryName > countryOne.countryName) {
        return -1;
      }
      return 0;
    });

    return countriesList;
  }

  function translateModal() {
    const langCode = getUserLocale().substring(0, 2);
    const translations = (window.redirectModalStrings[langCode])
      ? window.redirectModalStrings[langCode]
      : window.redirectModalStrings.en;
    if (translations) {
      Object.keys(translations).forEach((item) => {
        const element = document.querySelector(`[js-redirect-translation="${item}"]`);
        element.innerHTML = translations[item];
      });
    }
  }

  /**
   * Update modal template to reflect store settings in user's cookie.
   * - Replace choice card templates.
   * - Update country selector and re-render options based on selected.
   */
  function renderTemplates() {

    /**
     * FR is being used in storeCode ternary condition because an EU
     * country code does not exist in country selector.
     */
    const storeCode = getDefaultStoreCode();
    const matchingStore = Redirects().getMatchingStore(storeCode);
    const matchingOption = [...document.querySelector(selectors.countrySelector).children].find((element) => {
      return (element.value === storeCode);
    });

    document.querySelector(selectors.countrySelector).selectedIndex = matchingOption.index;
    setFlag(storeCode);
    updateOptions(matchingStore);
    updateSelectedLanguageOption();
  }

  /**
   * Handle settings form submission.
   * @param {Object} event - Form event.
   */
  function handleFormSubmit(event) {
    event.preventDefault();
    handleURLRedirection(document.querySelector(selectors.form));
  }

  /**
   * Main handler to redirect users to the correct store based on form selection.
   * - Set loading states and redirect user after form submission.
   * - Update data-store-url on form button.
   * - Use form data as query string.
   * @param {HTMLElement} form - Setting form element.
   */
  function handleURLRedirection(form) {
    const button = document.querySelector(selectors.updateButton);
    const {
      lang: selectedCountryLanguage,
      loc: selectedCountryCode,
    } = formToJSON(form);
    const selectedCountry = getCountryNameFromCode(selectedCountryCode);

    setButtonLoadingState(button);

    Cookies.set(cookieSelector.settings, Object.assign(formToJSON(form), { countryName: selectedCountry }), { domain: '.alessi.com', expires: 7 });

    /**
     * Check if user needs to be redirected to another store using current pathname.
     * - If not, then set a cookie and refresh the page.
     * - Otherwise, redirect the user to the correct store with query parameters.
     */
    window.setTimeout(() => {
      setButtonActiveState(button);
      Cookies.set(cookieSelector.modal, 'dismissed', { domain: '.alessi.com', expires: 7 });

      window.dataLayer.push({
        event: 'GAevent',
        eventID: '07',
        eventCategory: 'change_site_popup',
        eventAction: 'store_settings',
        eventLabel: selectedCountryCode,
        moreInfo: selectedCountryLanguage,
      });

      if (isUserRedirected(button.dataset.storeUrl)) {
        if (selectedCountryCode === 'AU' || selectedCountryCode === 'HK' || selectedCountryCode === 'CN') {
          window.location = button.dataset.storeUrl;
          return;
        }

        window.location = `${button.dataset.storeUrl.replace(/\/$/, '')}${isNewLanguage(form, selectedCountryLanguage, selectedCountryCode)}${getNewPathName()}?${serialize(form)}`;
        return;
      }

      window.location = `${button.dataset.storeUrl.replace(/\/$/, '')}${isNewLanguage(form, selectedCountryLanguage, selectedCountryCode)}${getNewPathName()}?${serialize(form)}`;
    }, 1500);
  }

  /**
   * Check if user will be redirected outside of current store.
   * @param {String} redirectUrl - The url dynamically generated in the form submit button.
   * @returns {Boolean}
   */
  function isUserRedirected(redirectUrl) {
    return (window.location.origin !== redirectUrl.replace(/\/$/, ''));
  }

  /**
   * Generate the language url params string.
   * @param {HTMLObject} form - submitted form
   * @param {String} selectedCountryLanguage - Selected language.
   * @param {String} selectedCountryCode - Selected country code.
   */
  function isNewLanguage(form, selectedCountryLanguage) {
    return (multipleOptions(form, selectors.languageSelector) && !isDefaultLanguage(form, selectedCountryLanguage))
      ? `/${selectedCountryLanguage.toLowerCase()}`
      : '';
  }

  /**
   *
   * @param {*} selectedCountryLanguage
   * @param {*} selectedCountryCode
   */
  function isDefaultLanguage(form, selectedCountryLanguage) {
    const button = form.querySelector(selectors.updateButton);
    return button.dataset.defaultLanguage === selectedCountryLanguage;
  }

  /**
   * Generate the new pathname removing the language part if in langify.
   */
  function getNewPathName() {
    const pathnameArray = window.location.pathname.split('/');
    return (window.theme.currentLanguage === pathnameArray[1])
      ? window.location.pathname.substring(3)
      : window.location.pathname;
  }

  /**
   * Check if the select element has more than one option.
   * @param {HTMLElement} form - submitted form.
   * @param {string} selectSelector - selector of object.
   */
  function multipleOptions(form, selectSelector) {
    const select = form.querySelector(selectSelector);
    return [...select.querySelectorAll('option')].length > 1;
  }

  /**
   * Handle change events from country selector
   * @param {Object} event - Change event from selector.
   */
  function handleCountryChange(event) {
    const selectedOption = event.target.options[event.target.selectedIndex];
    const matchingStore = Redirects().getMatchingStore(selectedOption.value);
    updateOptions(matchingStore);
    setFlag(selectedOption.value);
  }

  /**
   * Render new options in language and currency selector.
   * @param {Object} store - Store object from multi-store config.
   */
  function updateOptions(store) {
    const supportedLanguages = store.supportedLanguages.split(', ');

    const languageSelector = document.querySelector(selectors.languageSelector);
    const languageSelectorContainer = document.querySelector(selectors.languageSelectorContainer);

    languageSelector.innerHTML = getOptionTemplate(supportedLanguages);
    if (supportedLanguages.length > 1) {
      languageSelectorContainer.classList.remove('visually-hidden');
      languageSelector.setAttribute('tabindex', 0);
    } else {
      languageSelectorContainer.classList.add('visually-hidden');
      languageSelector.setAttribute('tabindex', -1);
    }

    document.querySelector(selectors.updateButton).dataset.storeUrl = store.storeUrl;
    document.querySelector(selectors.updateButton).dataset.defaultLanguage = store.defaultLanguage;
  }

  /**
   * Get option templates from array.
   * @param {Array} options - Supported options based on matched store.
   * @returns {String}
   */
  function getOptionTemplate(options) {
    return options.map((option) => {
      return `
        <option value="${option}">
          ${option}
        </option>`;
    }).join('');
  }

  function setFlag(country) {
    const flagContainer = document.querySelector(selectors.icon);
    flagContainer.innerHTML = `<span class="choice-card__icon flag-icon flag-icon-${country.toLowerCase()}"></span>`;
  }

  function setCountry(country) {
    document.querySelector(selectors.countrySelector).value = country;
  }

  /**
   * Check if click target is continue browsing choice.
   * @param {Object} target - Click target on container.
   * @returns {Boolean}
   */
  function isContinueBrowsingButton(target) {
    return (
      typeof target.attributes[dataSelectors.redirectModal] !== 'undefined' &&
      target.getAttribute(dataSelectors.redirectModal) === 'choiceContinue'
    );
  }

  /**
   * Check if click target is a redirect choice button.
   * @param {Object} target - Click target on container.
   * @returns {Boolean}
   */
  function isRedirectChoice(target) {
    return (
      typeof target.attributes[dataSelectors.redirectModal] !== 'undefined' &&
      target.getAttribute(dataSelectors.redirectModal) === 'redirectLink'
    );
  }

  /**
   * Check if click target is a close button.
   * @param {Object} target - Click target on container.
   * @returns {Boolean}
   */
  function isCloseButton(target) {
    return (
      typeof target.attributes[dataSelectors.redirectModal] !== 'undefined' &&
      target.getAttribute(dataSelectors.redirectModal) === 'close'
    );
  }

  /**
   * Handle continue button choice click.
   * @param {Event} event - Click target of continue link.
   */
  function handleContinueClick(event) {
    event.preventDefault();
    Cookies.set(cookieSelector.modal, 'dismissed', { expires: 7 });
  }

  /**
   * Handle parameter events when url string contains all parameters.
   * @param {String} query - Query parameter string.
   */
  function handleParameterEvents(query) {
    Cookies.set(cookieSelector.settings, query, { domain: '.alessi.com', expires: 7 });
    Cookies.set(cookieSelector.modal, 'dismissed', { domain: '.alessi.com', expires: 7 });
  }

  /**
   * Handle cookie and events based on location query string.
   * - Get query parameters and update store settings cookie.
   * - Update store icon with `Redirects:updateIcon` event.
   */
  function handleLocationParameterEvents() {
    const queryParameters = getQueryParamsObject();
    if (queryParameters.loc) {
      Frame.EventBus.emit(eventSelector.redirectIcon, queryParameters.loc.toLowerCase());
      const storeSettings = Cookies.getJSON(cookieSelector.settings);
      const selectedCountryName = getCountryNameFromCode(queryParameters.loc);

      const newSettings = Object.assign(storeSettings, {
        loc: queryParameters.loc.toLowerCase(),
        countryName: selectedCountryName,
      });
      Cookies.set(cookieSelector.settings, newSettings, { domain: '.alessi.com', expires: 7 });
    }
  }

  /**
   * Replace country code in title and body content.
   * Placeholder options defined in settings.
   * @param {Object} response - GeoIP response from `redirect.js`.
   */
  function render(response) {
    renderPlaceholderText(response);
  }

  /**
   * Replace location references in modal container.
   * @param {Object} response - Redirect store response defined in `redirect.js`.
   */
  function renderPlaceholderText(response) {

    /**
     * Replace choice content.
     */
    container.innerHTML = container.innerHTML.replace(
      placeholder.redirectStoreUrl, response.matchingStore.storeUrl,
    );

    container.innerHTML = container.innerHTML.replace(
      new RegExp(placeholder.redirectStoreCode, 'g'), response.location.countryCode.toLowerCase(),
    );

    /**
     * Replace current location content.
     */
    container.innerHTML = container.innerHTML.replace(
      new RegExp(placeholder.redirectStoreName, 'g'), response.location.countryName,
    );
  }

  /**
   * Handle close callback function by setting cookie.
   */
  function handleCloseButtonEvent() {
    modal.close(settings.id);
    handleCloseEvents();
  }

  /**
   * Handle close callback function by setting cookie.
   */
  function handleCloseEvents() {
    Cookies.set(cookieSelector.modal, 'dismissed', { domain: '.alessi.com', expires: 7 });
    document.body.style.overflow = '';
    document.body.classList.remove('modal-settings');
  }

  /**
   * Update language selector to use default selected language in settings.
   */
  function updateSelectedLanguageOption() {
    const selectedLanguage = getDefaultLanguageCode();
    const languageSelector = document.querySelector(selectors.languageSelector);
    const selectedOption = [...languageSelector.children].find((option) => option.value === selectedLanguage);

    if (typeof selectedOption === 'undefined') { return; }

    document.querySelector(selectors.languageSelector).selectedIndex = selectedOption.index;
  }

  /**
   * Get store and country code using theme settings and cookies.
   * @returns {String}
   */
  function getDefaultStoreCode() {
    if (typeof Cookies.getJSON(cookieSelector.settings) !== 'undefined') {
      return Cookies.getJSON(cookieSelector.settings).loc.toUpperCase();
    }

    return (theme.store.storeCode === 'EU') ? 'IT' : theme.store.storeCode;
  }

  /**
   * Get country name from country selector using country code.
   * @param {String} countryCode - Country code to filter values against.
   * @returns {String}
   */
  function getCountryNameFromCode(countryCode) {
    return [...document.querySelector(selectors.countrySelector).children].find((element) => {
      return element.value === countryCode.toUpperCase();
    }).text;
  }

  /**
   * Get default language setting.
   * @param {String} storeCode - Store code to find in multi-store configuration.
   * @returns {String}
   */
  function getDefaultLanguageCode(storeCode) {
    if (storeCode) {
      const matchingStore = Redirects().getMatchingStore(storeCode);
      return matchingStore.supportedLanguages.split(',')[0];
    }

    if (typeof Cookies.getJSON(cookieSelector.settings) !== 'undefined') {
      return Cookies.getJSON(cookieSelector.settings).lang;
    }

    const languageSelector = document.querySelector(selectors.languageSelector);
    return languageSelector.options[languageSelector.selectedIndex].value;
  }

  /**
   * Update form button to use loading state and disable.
   * @param {HTMLElement} button - Targeted form button.
   */
  function setButtonLoadingState(button) {
    button.setAttribute('disabled', true);
    button.classList.add(cssClasses.disabled);
    button.innerHTML = theme.redirect.updating;
  }

  /**
   * Return form button to active state and enable.
   * @param {HTMLElement} button - Targeted form button.
   */
  function setButtonActiveState(button) {
    button.removeAttribute('disabled');
    button.classList.remove(cssClasses.disabled);
    button.innerHTML = theme.redirect.updatePreferences;
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
